import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { 
  Users, 
  Calendar, 
  Gamepad2, 
  Settings, 
  UserPlus, 
  UserMinus,
  MessageCircle,
  Heart,
  Share2,
  ArrowLeft,
  MessageSquare,
  Pin,
  Plus,
  Edit2,
  Trash2,
  Send,
  Image,
  MoreHorizontal,
  BarChart3,
  PenTool
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/hooks/auth/AuthProvider";
import { useRole } from "@/hooks/useRole";
import { CreatePostModal } from "@/components/community/CreatePostModal";
import { 
  fetchCommunityBySlug, 
  fetchUserCommunityStatus, 
  fetchCommunityStats,
  joinCommunity,
  leaveCommunity 
} from "@/services/communityService";
import {
  createCommunityPost,
  fetchCommunityPosts,
  togglePostLike,
  deleteCommunityPost,
  updatePost
} from "@/services/communityPostService";
import { fetchCommunityCategories } from "@/services/postCategoryService";
import { 
  CommunityWithDetails, 
  UserCommunityStatus, 
  CommunityStats,
  CommunityPost,
  PostCategory,
  CreatePostData
} from "@/types/community";
import RichTextEditor from "@/components/ui/RichTextEditor";
import { supabase } from "@/integrations/supabase/client";
import { EditPostModal } from "@/components/community/EditPostModal";
import PostCard from "@/components/community/PostCard";
import TopMembersRanking from "@/components/community/TopMembersRanking";

const CommunityPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { authUser } = useRole();
  const { toast } = useToast();

  const [community, setCommunity] = useState<CommunityWithDetails | null>(null);
  const [userStatus, setUserStatus] = useState<UserCommunityStatus | null>(null);
  const [loading, setLoading] = useState(true);

  // Estados para posts
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);

  // Estados para categorias
  const [categories, setCategories] = useState<PostCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Estados para criação de post
  const [creatingPost, setCreatingPost] = useState(false);

  // Estados para interações dos posts
  const [likingPosts, setLikingPosts] = useState<Set<string>>(new Set());
  const [userLikes, setUserLikes] = useState<Record<string, boolean>>({});
  const [showComments, setShowComments] = useState<Record<string, boolean>>({});
  const [deletingPosts, setDeletingPosts] = useState<Set<string>>(new Set());
  const [deletingPostId, setDeletingPostId] = useState<string | null>(null);
  const [editingPost, setEditingPost] = useState<CommunityPost | null>(null);
  const [updatingPost, setUpdatingPost] = useState(false);
  
  // Trigger para atualizar ranking quando há novas interações
  const [rankingRefreshTrigger, setRankingRefreshTrigger] = useState(0);

  useEffect(() => {
    if (slug) {
      loadCommunityData();
    }
  }, [slug, user]);

  // Recarregar posts quando a categoria selecionada mudar
  useEffect(() => {
    if (community?.id) {
      loadCommunityPosts(community.id);
    }
  }, [selectedCategory]);

  const loadCommunityData = async () => {
    if (!slug) return;

    try {
      setLoading(true);
      console.log('🔍 [CommunityPage] Carregando dados da comunidade...');

      // Buscar dados da comunidade
      const communityData = await fetchCommunityBySlug(slug);
      setCommunity(communityData);

      console.log('🏠 [CommunityPage] Comunidade carregada:', communityData);
      console.log('👤 [CommunityPage] Dados do mentor:', communityData.mentor);
      console.log('📸 [CommunityPage] Avatar do mentor:', communityData.mentor?.avatar_url);
      console.log('📸 [CommunityPage] Image URL do mentor:', communityData.mentor?.image_url);
      console.log('🔗 [CommunityPage] Slug do mentor:', communityData.mentor?.slug);

      // Buscar status de membro se o usuário estiver logado
      if (user) {
        const membershipStatus = await fetchUserCommunityStatus(communityData.id, user.id);
        setUserStatus(membershipStatus);
      }

      // Carregar posts da comunidade
      await loadCommunityPosts(communityData.id);

      // Temporariamente desabilitado para evitar erro 404
      // await loadCommunityCategories(communityData.id);

    } catch (error) {
      console.error('Erro ao carregar dados da comunidade:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os dados da comunidade.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadCommunityPosts = async (communityId: string) => {
    try {
      setPostsLoading(true);
      console.log('📋 [loadCommunityPosts] Buscando posts para comunidade:', communityId);
      
      const response = await fetchCommunityPosts({
        community_id: communityId,
        // category_id: selectedCategory !== 'all' ? selectedCategory : undefined, // Temporariamente desabilitado
        limit: 20,
        offset: 0
      });
      
      console.log('✅ [loadCommunityPosts] Posts carregados:', response.posts.length);
      console.log('📋 [loadCommunityPosts] Posts detalhados:', response.posts);
      setPosts(response.posts);
      
      // Carregar likes do usuário se estiver logado
      if (user && response.posts.length > 0) {
        await loadUserLikes(response.posts.map(post => post.id));
      }
      
    } catch (error) {
      console.error('❌ [loadCommunityPosts] Erro ao carregar posts:', error);
      setPosts([]);
    } finally {
      setPostsLoading(false);
    }
  };

  const loadCommunityCategories = async (communityId: string) => {
    try {
      console.log('🏷️ [loadCommunityCategories] Buscando categorias para comunidade:', communityId);
      
      const categoriesData = await fetchCommunityCategories(communityId);
      
      console.log('✅ [loadCommunityCategories] Categorias carregadas:', categoriesData.length);
      setCategories(categoriesData);
      
    } catch (error) {
      console.error('❌ [loadCommunityCategories] Erro ao carregar categorias:', error);
      setCategories([]);
    }
  };

  // Função para carregar likes do usuário
  const loadUserLikes = async (postIds: string[]) => {
    if (!user) return;
    
    try {
      console.log('❤️ [loadUserLikes] Carregando likes do usuário para posts:', postIds.length);
      
      const { data: likes, error } = await supabase
        .from('community_post_likes')
        .select('post_id')
        .eq('user_id', user.id)
        .in('post_id', postIds);
      
      if (error) {
        console.error('❌ [loadUserLikes] Erro ao buscar likes:', error);
        return;
      }
      
      const likesMap: Record<string, boolean> = {};
      postIds.forEach(postId => {
        likesMap[postId] = likes?.some(like => like.post_id === postId) || false;
      });
      
      console.log('✅ [loadUserLikes] Likes carregados:', Object.keys(likesMap).filter(key => likesMap[key]).length);
      setUserLikes(likesMap);
      
    } catch (error) {
      console.error('❌ [loadUserLikes] Erro ao carregar likes:', error);
    }
  };

  const handleJoinCommunity = async () => {
    if (!user || !community) return;
    
    try {
      await joinCommunity(community.id, user.id);
      toast({
        title: "Sucesso!",
        description: "Você entrou na comunidade!",
      });
      // Recarregar status
      const status = await fetchUserCommunityStatus(community.id, user.id);
      setUserStatus(status);
    } catch (error) {
      toast({
        title: "Erro",
        description: "Falha ao entrar na comunidade. Tente novamente.",
        variant: "destructive",
      });
    }
  };

  const handleLeaveCommunity = async () => {
    if (!user || !community) return;
    
    try {
      await leaveCommunity(community.id, user.id);
      toast({
        title: "Você saiu da comunidade",
        description: "Você pode voltar a qualquer momento.",
      });
      // Recarregar status
      const status = await fetchUserCommunityStatus(community.id, user.id);
      setUserStatus(status);
    } catch (error) {
      toast({
        title: "Erro",
        description: "Falha ao sair da comunidade. Tente novamente.",
        variant: "destructive",
      });
    }
  };

  const handleCreatePostFromModal = async (postData: CreatePostData) => {
    if (!user || !community) {
      throw new Error("Usuário ou comunidade não encontrados");
    }

    try {
      setCreatingPost(true);
      
      // Detectar se há imagens no conteúdo
      const hasImage = postData.image_url || postData.content.includes('<img');
      
      const fullPostData = {
        title: postData.title,
        content: postData.content,
        community_id: postData.community_id,
        author_id: user.id,
        post_type: (hasImage ? 'image' : 'text') as 'text' | 'image' | 'video' | 'file',
        is_pinned: false,
        ...(postData.image_url && { media_url: postData.image_url }),
        ...(postData.category_id && { category_id: postData.category_id })
      };

      console.log('📝 [handleCreatePostFromModal] Criando post:', fullPostData);

      const newPost = await createCommunityPost(fullPostData);
      
      toast({
        title: "Sucesso!",
        description: "Post criado com sucesso!",
      });

    } catch (error: any) {
      console.error('❌ [handleCreatePostFromModal] Erro ao criar post:', error);
      
      let errorMessage = "Falha ao criar post. Tente novamente.";
      
      if (error.message) {
        if (error.message.includes('tabela')) {
          errorMessage = "Sistema de posts temporariamente indisponível.";
        } else if (error.message.includes('permissão')) {
          errorMessage = "Você não tem permissão para postar nesta comunidade.";
        } else {
          errorMessage = error.message;
        }
      }
      
      throw new Error(errorMessage);
    } finally {
      setCreatingPost(false);
    }
  };

  // Função para dar/remover like em post
  const handlePostLike = async (postId: string) => {
    if (!user) {
      toast({
        title: "Login necessário",
        description: "Você precisa estar logado para curtir posts.",
        variant: "destructive",
      });
      return;
    }

    if (likingPosts.has(postId)) return; // Evitar múltiplos cliques

    try {
      setLikingPosts(prev => new Set(prev).add(postId));
      
      const isLiked = await togglePostLike(postId, user.id);
      
      // Atualizar estado de likes do usuário
      setUserLikes(prev => ({ ...prev, [postId]: isLiked }));
      
      // Atualizar contador no post
      setPosts(prev => prev.map(post => 
        post.id === postId 
          ? { 
              ...post, 
              likes_count: isLiked 
                ? (post.likes_count || 0) + 1 
                : Math.max((post.likes_count || 0) - 1, 0)
            }
          : post
      ));

      toast({
        title: isLiked ? "Post curtido!" : "Like removido",
        description: isLiked ? "Você curtiu este post" : "Você removeu o like deste post",
      });

      // Atualizar ranking após interação
      setRankingRefreshTrigger(prev => prev + 1);

    } catch (error: any) {
      console.error('Erro ao curtir post:', error);
      toast({
        title: "Erro",
        description: error.message || "Falha ao curtir post. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLikingPosts(prev => {
        const newSet = new Set(prev);
        newSet.delete(postId);
        return newSet;
      });
    }
  };

  // Função para toggle comentários
  const handleToggleComments = (postId: string) => {
    setShowComments(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  // Função para compartilhar post
  const handleSharePost = async (post: CommunityPost) => {
    const shareUrl = `${window.location.origin}/comunidades/${community?.slug}#post-${post.id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title || 'Post da comunidade',
          text: post.content.substring(0, 100) + '...',
          url: shareUrl,
        });
      } catch (error) {
        console.log('Compartilhamento cancelado ou falhou');
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast({
          title: "Link copiado!",
          description: "O link do post foi copiado para a área de transferência.",
        });
      } catch (error) {
        toast({
          title: "Erro",
          description: "Não foi possível copiar o link. Tente novamente.",
          variant: "destructive",
        });
      }
    }
  };

  // Função para editar post
  const handleEditPost = (post: CommunityPost) => {
    setEditingPost(post);
  };

  // Função para atualizar post
  const handleUpdatePost = async (postId: string, data: { title: string; content: string; image_url?: string }) => {
    try {
      setUpdatingPost(true);
      console.log('✏️ [handleUpdatePost] Atualizando post:', postId, data);
      
      await updatePost(postId, {
        title: data.title,
        content: data.content,
        image_url: data.image_url
      });
      
      // Recarregar posts para refletir mudanças
      if (community?.id) {
        await loadCommunityPosts(community.id);
      }
      
    } catch (error: any) {
      console.error('❌ [handleUpdatePost] Erro ao atualizar post:', error);
      throw error; // Re-throw para que o modal possa tratar
    } finally {
      setUpdatingPost(false);
    }
  };

  // Função para deletar post
  const handleDeletePost = async (postId: string) => {
    if (!user) {
      toast({
        title: "Erro de autenticação",
        description: "Você precisa estar logado para deletar posts.",
        variant: "destructive",
      });
      return;
    }

    const confirm = window.confirm('Tem certeza que deseja deletar este post? Esta ação não pode ser desfeita.');
    if (!confirm) return;

    try {
      setDeletingPosts(prev => new Set(prev).add(postId));
      
      console.log('🗑️ [handleDeletePost] Iniciando deleção do post:', postId);
      
      await deleteCommunityPost(postId, user.id);
      
      console.log('✅ [handleDeletePost] Post deletado com sucesso');
      
      toast({
        title: "Post deletado!",
        description: "O post foi removido com sucesso.",
      });

      // Recarregar os posts para refletir a mudança
      if (community?.id) {
        await loadCommunityPosts(community.id);
      }

    } catch (error: any) {
      console.error('❌ [handleDeletePost] Erro ao deletar post:', error);
      toast({
        title: "Erro ao deletar",
        description: error.message || "Não foi possível deletar o post. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setDeletingPosts(prev => {
        const newSet = new Set(prev);
        newSet.delete(postId);
        return newSet;
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Determinar se o usuário é o dono da comunidade
  const isOwner = user && community && (user.id === community.mentor_id || user.id === community.owner_id);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0F16] flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!community) {
    return (
      <div className="min-h-screen bg-[#0A0F16] flex items-center justify-center">
        <div className="text-center text-white">
          <h1 className="text-2xl font-bold mb-4">Comunidade não encontrada</h1>
          <p className="text-gray-400 mb-6">A comunidade que você está procurando não existe.</p>
          <Button onClick={() => navigate('/comunidades')}>
            Voltar para Comunidades
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0F16]">
      {/* Header com Imagem de Capa */}
      <div className="relative">
        {community.cover_image_url ? (
          <div className="h-64 md:h-80 bg-cover bg-center relative" style={{ backgroundImage: `url(${community.cover_image_url})` }}>
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-black/40" />
          </div>
        ) : (
          <div className="h-64 md:h-80 bg-gradient-to-br from-[#00B6F0]/30 to-[#1AD3F9]/20 relative">
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          </div>
        )}

        {/* Botão Voltar */}
        <Button
          onClick={() => navigate('/comunidades')}
          variant="ghost"
          size="sm"
          className="absolute top-4 left-4 text-white hover:bg-white/20"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>

        {/* Botão de Configurações para o Dono */}
        {isOwner && (
          <Link 
            to={`/painel-mentor/comunidades/${community.id}/editar`}
            className="absolute top-4 right-4"
          >
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20"
            >
              <Settings className="h-4 w-4 mr-2" />
              Gerenciar Comunidade
            </Button>
          </Link>
        )}
      </div>

      {/* Conteúdo Principal */}
      <div className="container mx-auto px-4 -mt-20 relative z-10 mb-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Coluna Principal */}
          <div className="lg:col-span-2 space-y-4 lg:space-y-6 pb-16">
            {/* Card Principal da Comunidade */}
            <Card className="bg-[#1A2333] border-[#222A35]">
              <CardHeader className="pb-3 lg:pb-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 lg:gap-3">
                      <Badge variant="secondary" className="bg-[#00B6F0] text-black text-xs lg:text-sm">
                        <Gamepad2 className="h-3 w-3 lg:h-3 lg:w-3 mr-1" />
                        {community.game?.name || 'Jogo Desconhecido'}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl lg:text-2xl text-white leading-tight">
                      {community.name}
                    </CardTitle>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-4 lg:space-y-6">
                {/* Descrição */}
                <div>
                  <p className="text-gray-300 leading-relaxed text-sm lg:text-base">
                    {community.description}
                  </p>
                </div>

                {/* Ações da Comunidade */}
                <div className="flex gap-2 lg:gap-3 flex-wrap">
                  {user ? (
                    <>
                      {isOwner ? (
                        <div className="flex gap-2 lg:gap-3 flex-wrap">
                          <Badge variant="outline" className="bg-[#00B6F0]/10 text-[#00B6F0] border-[#00B6F0] text-xs lg:text-sm">
                            Você é o criador desta comunidade
                          </Badge>
                          <Link to={`/painel-mentor/comunidades/${community.id}/dashboard`}>
                            <Button variant="outline" size="sm" className="border-[#00B6F0] text-[#00B6F0] hover:bg-[#00B6F0] hover:text-black text-xs lg:text-sm">
                              <BarChart3 className="h-3 w-3 lg:h-4 lg:w-4 mr-1 lg:mr-2" />
                              <span className="hidden sm:inline">Ver </span>Estatísticas
                            </Button>
                          </Link>
                        </div>
                      ) : userStatus?.is_member ? (
                        <Button 
                          onClick={handleLeaveCommunity}
                          variant="outline" 
                          size="sm"
                          className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white text-xs lg:text-sm"
                        >
                          <UserMinus className="h-3 w-3 lg:h-4 lg:w-4 mr-1 lg:mr-2" />
                          Sair da Comunidade
                        </Button>
                      ) : (
                        <Button 
                          onClick={handleJoinCommunity}
                          size="sm"
                          className="bg-[#00B6F0] text-black hover:bg-[#1AD3F9] text-xs lg:text-sm"
                        >
                          <UserPlus className="h-3 w-3 lg:h-4 lg:w-4 mr-1 lg:mr-2" />
                          Entrar na Comunidade
                        </Button>
                      )}
                    </>
                  ) : (
                    <Link to="/login">
                      <Button size="sm" className="bg-[#00B6F0] text-black hover:bg-[#1AD3F9] text-xs lg:text-sm">
                        <UserPlus className="h-3 w-3 lg:h-4 lg:w-4 mr-1 lg:mr-2" />
                        Faça Login para Participar
                      </Button>
                    </Link>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Área de Criação de Post - Só para o Dono */}
            {isOwner && (
              <CreatePostModal
                communityId={community.id}
                communityName={community.name}
                onPostCreated={() => loadCommunityPosts(community.id)}
                onCreatePost={handleCreatePostFromModal}
                isCreating={creatingPost}
              />
            )}

            {/* Posts da Comunidade */}
            <Card className="bg-[#1A2333] border-[#222A35]">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <MessageCircle className="h-5 w-5" />
                    Posts da Comunidade ({posts.length})
                  </CardTitle>
                  
                  {/* Filtros por categoria - temporariamente desabilitado */}
                  {categories.length > 0 && false && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-400">Filtrar:</span>
                      <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                        <SelectTrigger className="w-40 bg-[#0F1419] border-[#222A35] text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A2333] border-[#222A35]">
                          <SelectItem value="all" className="text-white hover:bg-[#222A35]">
                            Todos
                          </SelectItem>
                          {categories.map((category) => (
                            <SelectItem 
                              key={category.id} 
                              value={category.id}
                              className="text-white hover:bg-[#222A35]"
                            >
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-2 h-2 rounded-full" 
                                  style={{ backgroundColor: category.color || '#00B6F0' }}
                                />
                                {category.name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {postsLoading ? (
                  <div className="text-center py-8">
                    <LoadingSpinner size="lg" />
                    <p className="text-gray-400 mt-4">Carregando posts...</p>
                  </div>
                ) : posts.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <MessageCircle size={48} className="mx-auto mb-4 opacity-50" />
                    <p>Ainda não há posts nesta comunidade.</p>
                    <p className="text-sm mt-2">
                      {isOwner 
                        ? "Seja o primeiro a compartilhar algo com sua comunidade!" 
                        : "Aguarde o mentor compartilhar conteúdo!"
                      }
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {posts.map((post) => (
                      <PostCard
                        key={post.id}
                        post={post}
                        onEditPost={handleEditPost}
                        onDeletePost={handleDeletePost}
                        isDeleting={deletingPostId === post.id}
                        onInteraction={() => setRankingRefreshTrigger(prev => prev + 1)}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 lg:space-y-6">
            {/* Informações do Mentor */}
            <Card className="bg-[#1A2333] border-[#222A35]">
              <CardHeader>
                <CardTitle className="text-white text-base lg:text-lg">Mentor</CardTitle>
              </CardHeader>
              <CardContent>
                <Link 
                  to={`/mentores/${community.mentor?.slug || community.mentor?.nickname || community.mentor?.id || 'mentor'}`}
                  className="flex items-center gap-3 p-2 lg:p-3 rounded-lg hover:bg-[#0F1419] transition-colors group"
                >
                  <Avatar className="h-10 w-10 lg:h-12 lg:w-12">
                    <AvatarImage 
                      src={community.mentor?.avatar_url || community.mentor?.image_url} 
                      alt={community.mentor?.nickname || community.mentor?.full_name || 'Mentor'}
                    />
                    <AvatarFallback className="bg-[#00B6F0] text-black text-sm lg:text-base">
                      {(community.mentor?.nickname || community.mentor?.full_name || 'M').charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="text-white font-medium group-hover:text-[#00B6F0] transition-colors text-sm lg:text-base">
                      {community.mentor?.nickname || community.mentor?.full_name || 'Mentor'}
                    </div>
                    <div className="text-xs lg:text-sm text-gray-400">Criador da comunidade</div>
                  </div>
                </Link>
              </CardContent>
            </Card>

            {/* Ranking dos Membros Mais Ativos */}
            <TopMembersRanking 
              communityId={community.id} 
              refreshTrigger={rankingRefreshTrigger}
            />

            {/* Informações da Comunidade */}
            <Card className="bg-[#1A2333] border-[#222A35]">
              <CardHeader>
                <CardTitle className="text-white text-base lg:text-lg">Informações</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 lg:space-y-4">
                <div className="flex items-center gap-3 text-gray-300">
                  <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <div>
                    <div className="text-xs lg:text-sm text-gray-400">Criada em</div>
                    <div className="text-xs lg:text-sm">{formatDate(community.created_at)}</div>
                  </div>
                </div>

                <Separator className="bg-[#222A35]" />

                <div className="flex items-center gap-3 text-gray-300">
                  <Users className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <div>
                    <div className="text-xs lg:text-sm text-gray-400">Total de membros</div>
                    <div className="text-xs lg:text-sm">{community.member_count} membros</div>
                  </div>
                </div>

                <Separator className="bg-[#222A35]" />

                <div className="flex items-center gap-3 text-gray-300">
                  <Gamepad2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <div>
                    <div className="text-xs lg:text-sm text-gray-400">Jogo</div>
                    <div className="text-xs lg:text-sm">{community.game?.name || 'Jogo Desconhecido'}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Regras da Comunidade */}
            <Card className="bg-[#1A2333] border-[#222A35]">
              <CardHeader>
                <CardTitle className="text-white text-base lg:text-lg">Regras da Comunidade</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 lg:space-y-3 text-xs lg:text-sm text-gray-300">
                  <div>1. Seja respeitoso com todos os membros</div>
                  <div>2. Mantenha as discussões relacionadas ao jogo</div>
                  <div>3. Não faça spam ou autopromção excessiva</div>
                  <div>4. Use linguagem apropriada</div>
                  <div>5. Siga as diretrizes da plataforma</div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Modal de Edição de Post */}
      <EditPostModal
        post={editingPost}
        isOpen={!!editingPost}
        onClose={() => setEditingPost(null)}
        onPostUpdated={() => {
          if (community?.id) {
            loadCommunityPosts(community.id);
          }
        }}
        onUpdatePost={handleUpdatePost}
        isUpdating={updatingPost}
      />
    </div>
  );
};

export default CommunityPage; 